import React from "react";

import { FiThermometer } from "react-icons/fi";

import {
  HiOutlineExclamationCircle,
  HiOutlineCheckCircle,
  HiOutlineHome,
} from "react-icons/hi2";

import { BiPackage, BiBug } from "react-icons/bi";

import { useIncident } from '../context/incidentContext';
import { complianceArea } from '../incidentData';

export default function IncidentAnalyticsPanels() {
  const { incidents, summary, loading, error, showOpen, setPanel, updateFilter } = useIncident();
  const agingData = summary.aging.map(item => ({ ...item, count: loading ? '…' : error ? '—' : item.count }));
  const complianceData = [
    {
      id: 1,
      label: "Health & Safety",
      icon: HiOutlineExclamationCircle,
      iconColor: "text-rose-500",
    },
    {
      id: 2,
      label: "Food Safety (Allergens)",
      icon: HiOutlineCheckCircle,
      iconColor: "text-emerald-500",
    },
    {
      id: 3,
      label: "Temperature Control",
      icon: FiThermometer,
      iconColor: "text-indigo-500",
    },
    {
      id: 4,
      label: "Premises & Environment",
      icon: HiOutlineHome,
      iconColor: "text-violet-600",
    },
    {
      id: 5,
      label: "Supplier / Traceability",
      icon: BiPackage,
      iconColor: "text-indigo-600",
    },
    {
      id: 6,
      label: "Pest Control",
      icon: BiBug,
      iconColor: "text-rose-600",
    },
  ].concat(incidents.some(row => complianceArea(row) === 'Other') ? [{ id: 7, label: 'Other', icon: HiOutlineExclamationCircle, iconColor: 'text-slate-500' }] : []).map(item => {
    const count = incidents.filter(row => complianceArea(row) === item.label).length;
    return { ...item, count: loading ? '…' : error || item.label === 'Pest Control' ? '—' : count,
      percentage: item.label === 'Pest Control' ? '—' : '(' + (incidents.length ? Math.round(count * 100 / incidents.length) : 0) + '%)' };
  });

  return (
    <div className="w-full max-w-[400px] flex flex-col gap-5 font-sans bg-slate-50/50 p-1">
      {/* =========================================================
          OPEN INCIDENTS BY AGING
      ========================================================= */}
      <div className="bg-white border border-slate-200/80 rounded-xl shadow-sm overflow-hidden flex flex-col justify-between min-h-[220px]">
        <div className="p-4">
          <h3 className="text-xs font-bold text-slate-900 tracking-tight mb-4">
            Open Incidents by Aging
          </h3>

          <div className="flex flex-col gap-3.5">
            {agingData.map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between text-xs"
              >
                {/* LABEL */}
                <span className="w-[85px] font-semibold text-slate-500 shrink-0">
                  {item.label}
                </span>

                {/* PROGRESS BAR */}
                <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden mx-4 relative">
                  <div
                    className={`h-full rounded-full ${item.barBg}`}
                    style={{
                      width: item.percentage,
                    }}
                  />
                </div>

                {/* COUNT */}
                <span className="w-4 text-right font-bold text-slate-800 shrink-0">
                  {item.count}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* FOOTER */}
        <div className="px-4 py-3 border-t border-slate-100 bg-slate-50/10">
          <button
            type="button"
            onClick={() => showOpen()}
            className="text-xs font-bold text-blue-600 hover:underline"
          >
            View all open incidents
          </button>
        </div>
      </div>

      {/* =========================================================
          INCIDENTS BY COMPLIANCE AREA
      ========================================================= */}
      <div className="bg-white border border-slate-200/80 rounded-xl shadow-sm overflow-hidden flex flex-col justify-between min-h-[300px]">
        <div className="p-4">
          <h3 className="text-xs font-bold text-slate-900 tracking-tight mb-4">
            Incidents by Compliance Area (Selected Range)
          </h3>

          <div className="flex flex-col gap-3">
            {complianceData.map((item) => {
              const Icon = item.icon;

              return (
                <div
                  key={item.id}
                  title={item.label === 'Pest Control' ? 'Pest-control incident classification is not tracked' : item.label}
                  role={item.label === 'Pest Control' ? undefined : 'button'}
                  tabIndex={item.label === 'Pest Control' ? undefined : 0}
                  onClick={() => { if (item.label !== 'Pest Control') updateFilter('category', item.label); }}
                  onKeyDown={event => { if (event.key === 'Enter' && item.label !== 'Pest Control') updateFilter('category', item.label); }}
                  className="flex items-center justify-between text-xs"
                >
                  {/* LEFT SIDE */}
                  <div className="flex items-center gap-2.5 min-w-0">
                    <Icon className={`w-4 h-4 shrink-0 ${item.iconColor}`} />

                    <span className="font-bold text-slate-700 truncate tracking-tight">
                      {item.label}
                    </span>
                  </div>

                  {/* RIGHT SIDE */}
                  <div className="flex items-center gap-3 shrink-0 ml-4">
                    <span className="font-extrabold text-slate-900">
                      {item.count}
                    </span>

                    <span className="text-[11px] font-medium text-slate-400 w-10 text-right">
                      {item.percentage}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* FOOTER */}
        <div className="px-4 py-3 border-t border-slate-100 bg-slate-50/10">
          <button
            type="button"
            onClick={() => setPanel({ kind: "breakdown" })}
            className="text-xs font-bold text-blue-600 hover:underline"
          >
            View full breakdown
          </button>
        </div>
      </div>
    </div>
  );
}
